import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setApiToken, clearApiToken } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]           = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  /**
   * On app load, try to silently restore the session via the refresh
   * cookie — except on /auth/magic, which performs its own explicit
   * token exchange (MagicLinkVerify.jsx's loginWithToken). Running both
   * at once is a real race: if this fetch (using whatever refresh cookie
   * was already sitting in the browser, e.g. from a different account on
   * a shared device) resolves AFTER the magic-link exchange sets the
   * fresh member into context, it silently overwrites the correct
   * just-logged-in member with the stale one — confirmed 2026-09-01,
   * a magic link for one member displayed a completely different
   * member's name/email because this effect won the race.
   */
  useEffect(() => {
    if (window.location.pathname.startsWith('/auth/magic')) {
      setIsLoading(false);
      return;
    }
    const restoreSession = async () => {
      try {
        const { data } = await api.post('/auth/refresh');
        // Update the module-level token FIRST so the interceptor is ready
        // before any child components mount and make their first API calls.
        setApiToken(data.accessToken);
        setAccessToken(data.accessToken);
        setUser(data.member);
      } catch {
        // No valid session — user needs to log in
      } finally {
        setIsLoading(false);
      }
    };
    restoreSession();
  }, []);

  /**
   * Auto-refresh: on 401 TOKEN_EXPIRED response, try /auth/refresh once.
   * If that also fails, log the user out.
   */
  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (
          error.response?.status === 401 &&
          error.response?.data?.code === 'TOKEN_EXPIRED' &&
          !original._retried
        ) {
          original._retried = true;
          try {
            const { data } = await api.post('/auth/refresh');
            setApiToken(data.accessToken);
            setAccessToken(data.accessToken);
            setUser(data.member);
            original.headers.Authorization = `Bearer ${data.accessToken}`;
            return api(original);
          } catch (refreshErr) {
            if (refreshErr.response?.data?.code === 'membership_inactive') {
              clearApiToken();
              setAccessToken(null);
              setUser(null);
              window.location.href = '/auth/login?error=membership_inactive';
            } else {
              clearApiToken();
              setAccessToken(null);
              setUser(null);
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(responseInterceptor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setApiToken(data.accessToken);
    setAccessToken(data.accessToken);
    setUser(data.member);
    return data.member;
  }, []);

  const signup = useCallback(async (email, password, name, phone) => {
    const { data } = await api.post('/auth/signup', { email, password, name, phone });
    setApiToken(data.accessToken);
    setAccessToken(data.accessToken);
    setUser(data.member);
    return data.member;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    clearApiToken();
    setAccessToken(null);
    setUser(null);
  }, []);

  /**
   * Called by the magic link verify page and Google OAuth callback.
   * If memberData is provided we skip the extra /members/me call.
   */
  const loginWithToken = useCallback(async (token, memberData) => {
    setApiToken(token);
    setAccessToken(token);
    if (memberData) {
      setUser(memberData);
    } else {
      const { data } = await api.get('/members/me');
      setUser(data);
    }
  }, []);

  /** Allow components (e.g. Onboarding) to update user fields in context. */
  const updateUser = useCallback((fields) => {
    setUser(prev => prev ? { ...prev, ...fields } : prev);
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    signup,
    logout,
    loginWithToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
